/**
 * A base class to be used for lazy loading JS.
 *
 * This Lazy Loader uses the Intersection Observer API.
 * HEAR YE! HEAR YE! READ ALL ABOUT IT: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 */

export default class LazyLoader {
  constructor(observeElements, initOnIntersect) {
    this.observer = null;
    this.observeElements = observeElements;
    this.initOnIntersect = initOnIntersect;
    this.options = {
      rootMargin: '450px 0px',
    };
  }

  /**
   * Handle intersection of element.
   *
   * @param entries
   */
  intersectHandler = (entries) => {
    entries.forEach((entry) => {
      // If slider is within threshold and has not been observed.
      if (entry.isIntersecting) {
        this.observer.unobserve(entry.target); // Unobserve once this has been observed.

        this.initOnIntersect(entry.target);
      }
    });
  };

  /**
   * Create observer.
   */
  createObserver = () => {
    if (!LazyLoader.isSupported) {
      throw new Error('Intersection Observer API not supported!');
    }

    if (!this.intersectHandler) {
      throw new Error('Needs a function specified for "intersectHandler"');
    }

    if (!this.observeElements) {
      throw new Error('Needs "observeElements" to be set!');
    }

    this.observer = new IntersectionObserver(
      this.intersectHandler,
      this.options
    );

    if (!this.observeElements.length) {
      this.observer.observe(this.observeElements);
      return;
    }

    this.observeElements.forEach((element) => this.observer.observe(element));
  };

  /**
   * Create observer, catching errors.
   */
  create() {
    try {
      this.createObserver();
    } catch (error) {
      console.error(`[LazyLoader]: ${error}`);
    }
  }

  /**
   * Fallback for when the Intersection Observer API is not supported.
   */
  notSupported = () => {
    if (!this.observeElements.length) {
      this.initOnIntersect(this.observeElements);
      return;
    }

    this.observeElements.forEach((element) => this.initOnIntersect(element));
  };

  /**
   * Initiate this class!
   *
   * @param observeElements
   * @param initOnIntersect
   */
  static init = (observeElements, initOnIntersect) => {
    const self = new LazyLoader(observeElements, initOnIntersect);

    if (LazyLoader.isSupported()) {
      self.create();
      return;
    }

    self.notSupported();
  };

  /**
   * Check support for intersection observer.
   */
  static isSupported = () => {
    if (
      !('IntersectionObserver' in window) ||
      !('IntersectionObserverEntry' in window) ||
      !('intersectionRatio' in window.IntersectionObserverEntry.prototype)
    ) {
      return false;
    }

    return true;
  };
}
